import React, { useEffect, useState } from "react";
import PublicHeader from "@/components/PublicHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, loadUser } from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy, Send, MessageSquare } from "lucide-react";

const CATEGORIES = ["Registration/Login","OTP/Email","Job Application","Assessment","Interview","Hackathon","Career Event","Profile/Resume","Technical Issue","Other"];
const PRIORITIES = ["Low","Normal","High","Urgent"];

export default function Support() {
  const user = loadUser();
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(null); // ticket detail
  const [f, setF] = useState({ category: "Job Application", priority: "Normal", subject: "", description: "" });
  const [reply, setReply] = useState("");

  const load = () => { if (user && user.role === "candidate") api.get("/support/tickets/mine").then(r => setTickets(r.data)); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.subject || !f.description) { toast.error("Subject and description are required"); return; }
    try {
      await api.post("/support/tickets", f);
      toast.success("Ticket created — we'll get back to you shortly.");
      setF({ category: "Job Application", priority: "Normal", subject: "", description: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openTicket = async (tid) => {
    const { data } = await api.get(`/support/tickets/${tid}`);
    setOpen(data);
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/support/tickets/${open.id}/replies`, { message: reply, internal: false });
      setReply("");
      openTicket(open.id);
      toast.success("Reply sent");
    } catch (e) { toast.error("Failed"); }
  };

  if (!user || user.role !== "candidate") {
    return <div><PublicHeader/><div className="max-w-4xl mx-auto p-10">
      <h1 className="font-display text-3xl font-bold text-[#0A2540]">Help & Support</h1>
      <p className="text-slate-600 mt-3">Please <a href="/login" className="text-[#2563EB] font-medium">sign in</a> as a candidate to view or create support tickets.</p>
    </div></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <div className="max-w-5xl mx-auto px-6 py-12" data-testid="support-page">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F59E0B]">Help & Support</div>
        <h1 className="font-display text-3xl font-bold text-[#0A2540] mt-2 flex items-center gap-2"><LifeBuoy className="w-6 h-6 text-[#2563EB]"/>How can we help?</h1>

        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          <Card className="p-6 border-slate-200">
            <h2 className="font-display font-semibold text-lg text-[#0A2540]">Raise a support ticket</h2>
            <p className="text-sm text-slate-500 mt-1">We respond within 1 business day. All updates arrive via email + your notification center.</p>
            <div className="mt-5 space-y-3">
              <div><Label>Category</Label>
                <Select value={f.category} onValueChange={v=>setF({...f, category:v})}>
                  <SelectTrigger data-testid="support-category"><SelectValue/></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Priority</Label>
                <Select value={f.priority} onValueChange={v=>setF({...f, priority:v})}>
                  <SelectTrigger data-testid="support-priority"><SelectValue/></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Subject</Label><Input value={f.subject} onChange={e=>setF({...f, subject:e.target.value})} data-testid="support-subject"/></div>
              <div><Label>Description</Label><Textarea rows={5} value={f.description} onChange={e=>setF({...f, description:e.target.value})} data-testid="support-description"/></div>
              <Button onClick={create} className="w-full bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="support-submit"><Send className="w-4 h-4 mr-2"/>Submit Ticket</Button>
            </div>
          </Card>

          <Card className="p-6 border-slate-200">
            <h2 className="font-display font-semibold text-lg text-[#0A2540]">My tickets</h2>
            {tickets.length === 0 ? <p className="text-sm text-slate-500 mt-3">You haven't raised any tickets yet.</p> : (
              <div className="mt-4 space-y-2">
                {tickets.map(t => (
                  <button key={t.id} onClick={()=>openTicket(t.id)} className="w-full text-left border border-slate-200 rounded-md p-3 hover:border-[#2563EB]" data-testid={`ticket-${t.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#0A2540] truncate">{t.subject}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{t.id} · {t.category}</div>
                      </div>
                      <Badge className={`border-none ${
                        t.status === "Resolved" || t.status === "Closed" ? "bg-emerald-100 text-emerald-700" :
                        t.status === "Waiting for Candidate" ? "bg-amber-100 text-amber-800" :
                        "bg-blue-100 text-blue-700"
                      }`}>{t.status}</Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <Dialog open={!!open} onOpenChange={(v)=>!v && setOpen(null)}>
        <DialogContent className="max-w-2xl" data-testid="ticket-detail-dialog">
          {open && <>
            <DialogHeader><DialogTitle>{open.subject}</DialogTitle></DialogHeader>
            <div className="text-xs text-slate-500">{open.id} · {open.category} · Priority {open.priority} · <Badge className="bg-slate-100 text-slate-700 border-none">{open.status}</Badge></div>
            <div className="mt-4 space-y-3 max-h-80 overflow-y-auto">
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-xs text-slate-500">{open.candidate_name} · {new Date(open.created_at).toLocaleString()}</div>
                <p className="text-sm text-[#0A2540] mt-1 whitespace-pre-wrap">{open.description}</p>
              </div>
              {(open.replies || []).map(r => (
                <div key={r.id} className={`rounded-md p-3 ${r.author_role === "candidate" ? "bg-blue-50 border border-blue-100" : "bg-emerald-50 border border-emerald-100"}`}>
                  <div className="text-xs text-slate-500">{r.author_name} ({r.author_role}) · {new Date(r.created_at).toLocaleString()}</div>
                  <p className="text-sm text-[#0A2540] mt-1 whitespace-pre-wrap">{r.message}</p>
                </div>
              ))}
            </div>
            {open.status !== "Closed" && (
              <div className="mt-4 flex gap-2">
                <Textarea rows={2} value={reply} onChange={e=>setReply(e.target.value)} placeholder="Type your reply…" data-testid="ticket-reply-input"/>
                <Button onClick={sendReply} className="bg-[#0A2540]" data-testid="ticket-reply-send"><MessageSquare className="w-4 h-4"/></Button>
              </div>
            )}
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
